
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Star, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { defaultImageService } from '@/services/defaultImageService';

export interface SolutionCardProps {
  id: string;
  name: string;
  category: string;
  shortDescription: string;
  tags?: string[];
  image?: string;
  featured?: boolean;
  search_type?: 'standard' | 'ai_vector';
  slug: string;
}

const SolutionCard = ({
  id,
  name,
  category,
  shortDescription,
  tags = [],
  image,
  featured = false,
  search_type,
  slug,
}: SolutionCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  // Get cached default image URL (24-hour cache)
  const defaultAIImage = defaultImageService.getDefaultImageUrlSync();
  
  // Use default image if no image provided or if image failed to load
  const displayImage = imageError || !image ? defaultAIImage : image;

  // Use slug for SEO-friendly URLs
  const solutionUrl = `/solution/${slug}`;

  return (
    <Link
      to={solutionUrl}
      className={cn(
        "group rounded-xl overflow-hidden transition-all duration-300",
        "flex flex-col h-full bg-card border border-border/40",
        "hover:border-primary/20 hover:shadow-lg",
        featured && "ring-1 ring-primary/30"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image section - always show with fallback */}
      <div className="aspect-video w-full overflow-hidden bg-secondary/30">
        <img
          src={displayImage}
          alt={name}
          className={cn(
            "w-full h-full object-contain transition-transform duration-700 ai-solution-image",
            isHovered && "scale-105"
          )}
          onError={() => setImageError(true)}
        />
      </div>

      {/* Card content */}
      <div className="flex flex-col flex-grow p-5">
        <div className="mb-2 relative flex items-center justify-center">
          {/* Left side - Featured indicator */}
          {featured && (
            <div className="absolute left-0 flex items-center gap-1">
              <Star className="h-3.5 w-3.5 fill-primary text-primary" />
              <span className="text-xs font-medium text-primary">Featured</span>
            </div>
          )}
          
          {/* Center - Category (always centered) */}
          <span className="text-xs font-medium text-muted-foreground px-2 py-1 bg-secondary rounded-full">
            {category}
          </span>
          
          {/* Right side - AI indicator */}
          {search_type === 'ai_vector' && (
            <div className="absolute right-0 flex items-center">
              <Sparkles className="h-3.5 w-3.5 text-purple-500" />
            </div>
          )}
        </div>

        <h3 className="text-lg font-medium group-hover:text-primary transition-colors">
          {name}
        </h3>

        <p className="mt-2 mb-4 text-sm text-muted-foreground line-clamp-3">
          {shortDescription}
        </p>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-2">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 bg-secondary/50 rounded-full text-secondary-foreground"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-xs px-2 py-0.5 bg-secondary/50 rounded-full text-secondary-foreground">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* View details footer */}
      <div className={cn(
        "p-4 flex items-center justify-between border-t border-border/40 bg-secondary/30",
        "transition-colors group-hover:bg-secondary/70"
      )}>
        <span className="text-xs font-medium">View details</span>
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>
    </Link>
  );
};

export default SolutionCard;
